import { useCallback } from 'react';
import { fetchJson } from '../../../helpers/fetch.js';

import {
  work_package_update_path,
  task_update_path,
  task_create_link_path,
  task_delete_link_path
} from '~/routes';

const formatDate = (value) => {
  if (!value) return null;
  const d = new Date(value);
  return d.toISOString().slice(0, 10);
};

export const useGanttUpdates = ({ projectId }) => {
  const addLink = useCallback(async ({ link }) => {
    try {
      const result = await fetchJson(
        task_create_link_path({
          project_id: projectId,
          source: link.source,
          target: link.target,
          link_type: link.type
        }),
        { method: 'POST', headers: { 'Accept': 'application/json' } }
      );

      if (result?.errors) return false;

      const newLink = {
        id: result.id,
        source: result.source ?? link.source,
        target: result.target ?? link.target,
        type: result.type ?? link.type,
      };

      return newLink;
    } catch {
      return false;
    }
  }, [projectId]);

  const deleteLink = useCallback(async ({ id }) => {
    try {
      if (!id) return false;
      const result = await fetchJson(
        task_delete_link_path({
          link_id: id,
          project_id: projectId
        }),
        { method: 'POST', headers: { 'Accept': 'application/json' } }
      );
      if (result?.errors) {
        return false;
      };
      return result;
    } catch {
      return false;
    }
  }, [projectId]);

  const updateTask = useCallback(async ({ id, task }) => {
    if (!projectId) return;

    const isWorkPackage = typeof id === 'string' && id.startsWith('wp:');
    const numericId = isWorkPackage ? id.slice(3) : id;

    const path = isWorkPackage
      ? work_package_update_path({ work_package_id: numericId, project_id: projectId })
      : task_update_path({ task_id: numericId });

    const key = isWorkPackage ? 'work_package' : 'task';

    const payload = {
      [key]: {
        [isWorkPackage ? 'name' : 'title']: task.text,
        description: task.description,
        start_date: formatDate(task.start),
        end_date: formatDate(task.end),
        completion_percentage: String(task.progress)
      }
    };

    const res = await fetchJson(path, {
      body: JSON.stringify(payload),
      method: 'POST'
    });
    return res;
  }, [projectId]);

  return {
    updateTask,
    addLink,
    deleteLink,
  };
};
